import { useCallback, useSyncExternalStore } from "react";

/**
 * One string kept in local storage, such as the project the user last chose. Local storage
 * is a convenience: unreadable values count as absent, and failed writes are ignored.
 */
export function readStoredString(storageKey: string): string | null {
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/** Store `value`, or remove the entry for `null`. */
export function writeStoredString(storageKey: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, value);
    }
  } catch {
    // Storage full or blocked: the choice lives for this page only.
  }
}

/** The views of this window that read each key with `useStoredString`. */
const readers = new Map<string, Set<() => void>>();
/** Each key's latest value in this page, so a choice holds even where storage refuses it. */
const latest = new Map<string, string | null>();
let followsOtherWindows = false;

/**
 * Another window's change makes storage the truth again, whether or not a view here reads the key
 * now (Sol S3): one listener for the page's lifetime, as long-lived as `latest`.
 */
function followOtherWindows(): void {
  if (followsOtherWindows) return;
  followsOtherWindows = true;
  window.addEventListener("storage", (event) => {
    const changed = event.key === null ? new Set([...latest.keys(), ...readers.keys()]) : [event.key];
    for (const storageKey of changed) {
      latest.delete(storageKey);
      for (const reader of readers.get(storageKey) ?? []) reader();
    }
  });
}

/** Store `value`, as `writeStoredString` does, and tell every view reading `storageKey` here. */
export function setStoredString(storageKey: string, value: string | null): void {
  followOtherWindows();
  latest.set(storageKey, value);
  writeStoredString(storageKey, value);
  for (const reader of readers.get(storageKey) ?? []) reader();
}

/**
 * The string stored under `storageKey`, for a choice every view shares, such as a sidebar's width:
 * the view re-renders when `setStoredString` changes it here, or another window changes it.
 */
export function useStoredString(storageKey: string): string | null {
  const subscribe = useCallback((reader: () => void) => {
    followOtherWindows();
    const own = readers.get(storageKey) ?? new Set<() => void>();
    readers.set(storageKey, own);
    own.add(reader);
    return () => { own.delete(reader); };
  }, [storageKey]);
  const read = () => (latest.has(storageKey) ? (latest.get(storageKey) ?? null) : readStoredString(storageKey));
  return useSyncExternalStore(subscribe, read, () => null);
}
